import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { formatRupiah, formatDate } from "@/lib/format";
import { ShoppingCart, ChevronDown, ChevronUp } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PayStateBadge, PAY_STATUS_LABEL } from "@/components/SalesShared";
import { AdminActions } from "@/components/AdminActions";
import { useBulk, BulkCheck, BulkCheckAll, BulkBar } from "@/components/BulkDelete";

export const TX_FIELDS = [
  { k: "date", l: "Tanggal", t: "date" }, { k: "nota_number", l: "Nomor Nota" }, { k: "customer_name", l: "Nama Pelanggan" },
  { k: "customer_phone", l: "No. HP" }, { k: "customer_address", l: "Alamat" }, { k: "amount", l: "Jumlah Tagihan", t: "money" },
  { k: "due_date", l: "Jatuh Tempo (kredit)", t: "date" },
];
const PAY_FIELDS = [{ k: "date", l: "Tanggal Bayar", t: "date" }, { k: "amount", l: "Jumlah", t: "money" }];
const CUST_FIELDS = [{ k: "name", l: "Nama" }, { k: "phone", l: "No. HP" }, { k: "address", l: "Alamat" }, { k: "note", l: "Catatan" }];

function TxRow({ t, onDone, bulk }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="border-t align-top hover:bg-secondary/40 cursor-pointer" onClick={() => setOpen((o) => !o)} data-testid={`admin-tx-row-${t.id}`}>
        <td className="pl-4 py-2.5 w-8"><BulkCheck bulk={bulk} k={`tx:${t.id}`} item={{ kind: "transactions", id: t.id }} testId={`bulk-tx-${t.id}`} /></td>
        <td className="px-4 py-2.5 whitespace-nowrap">{formatDate(t.date)}</td>
        <td className="px-4 py-2.5">{t.owner_name}<span className="block text-xs text-muted-foreground">{t.branch_name}</span></td>
        <td className="px-4 py-2.5 font-mono"><span className="inline-flex items-center gap-1">{t.payments?.length > 0 && (open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}{t.nota_number}</span></td>
        <td className="px-4 py-2.5">{t.customer_name}<span className="block text-xs text-muted-foreground">{t.customer_phone}</span></td>
        <td className="px-4 py-2.5">{PAY_STATUS_LABEL[t.pay_status]}{t.pay_status === "kredit" && <span className="ml-1"><PayStateBadge state={t.payment_state} /></span>}</td>
        <td className="px-4 py-2.5 text-right font-mono">{formatRupiah(t.amount)}</td>
        <td className="px-4 py-2.5 text-right font-mono">{formatRupiah(t.remaining)}</td>
        <td className="px-4 py-2.5 text-right"><AdminActions editPath={`/admin/records/transactions/${t.id}`} deletePath={`/admin/records/transactions/${t.id}`} fields={TX_FIELDS} values={t} onDone={onDone} title="Edit Transaksi" testId={`admin-tx-${t.id}`} confirmText="Transaksi beserta semua riwayat pembayarannya akan dihapus permanen dan hilang dari semua laporan, rekap, saldo kas, dan piutang." /></td>
      </tr>
      {open && (t.payments || []).map((p, i) => (
        <tr key={p.id || i} className="bg-secondary/40 text-xs" data-testid={`admin-tx-payment-${t.id}-${i}`}>
          <td className="px-4 py-2 pl-8" colSpan={6}>Pembayaran {formatDate(p.date)} · {p.method === "transfer" ? `Transfer ${p.company_name || ""}` : "Cash"}</td>
          <td className="px-4 py-2 text-right font-mono">{formatRupiah(p.amount)}</td>
          <td />
          <td className="px-4 py-2 text-right"><AdminActions editPath={`/admin/sub/tx-payments/${t.id}/${p.id || i}`} deletePath={`/admin/sub/tx-payments/${t.id}/${p.id || i}`} fields={PAY_FIELDS} values={p} onDone={onDone} title="Edit Pembayaran" testId={`admin-pay-${t.id}-${i}`} confirmText="Pembayaran akan dihapus permanen. Sisa piutang pelanggan bertambah kembali." /></td>
        </tr>
      ))}
    </>
  );
}

export default function AdminTransactions() {
  const [tab, setTab] = useState("tx");
  const [rows, setRows] = useState(null);
  const [custs, setCusts] = useState(null);
  const [q, setQ] = useState("");
  const bulk = useBulk();
  const load = useCallback(() => {
    api.get("/admin/transactions").then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/admin/customers").then((r) => setCusts(r.data)).catch(() => setCusts([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  const s = q.trim().toLowerCase();
  const txs = (rows || []).filter((r) => !s || [r.nota_number, r.customer_name, r.owner_name, r.branch_name].some((x) => (x || "").toLowerCase().includes(s)));
  const cs = (custs || []).filter((c) => !s || [c.name, c.phone, c.owner_name, c.address].some((x) => (x || "").toLowerCase().includes(s)));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}><ShoppingCart className="h-6 w-6 text-brand" /> Semua Transaksi</h2>
          <p className="text-sm text-muted-foreground">Input transaksi, riwayat pembayaran piutang, dan data customer seluruh Pemohon.</p>
        </div>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nota / pelanggan / pemohon" className="max-w-xs h-9" data-testid="admin-tx-search" />
      </div>
      <Tabs value={tab} onValueChange={(v) => { setTab(v); bulk.clear(); }}>
        <TabsList><TabsTrigger value="tx" data-testid="admin-tab-tx">Transaksi ({txs.length})</TabsTrigger><TabsTrigger value="cust" data-testid="admin-tab-cust">Customer ({cs.length})</TabsTrigger></TabsList>
      </Tabs>
      <BulkBar bulk={bulk} onDone={load} label={tab === "tx" ? "transaksi" : "customer"} />
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          {tab === "tx" ? (
            <table className="w-full text-sm" data-testid="admin-tx-table">
              <thead className="bg-secondary text-xs uppercase text-muted-foreground"><tr>
                <th className="pl-4 py-2.5 w-8"><BulkCheckAll bulk={bulk} entries={txs.map((t) => [`tx:${t.id}`, { kind: "transactions", id: t.id }])} testId="bulk-tx-all" /></th>
                <th className="text-left px-4 py-2.5">Tanggal</th><th className="text-left px-4 py-2.5">Pemohon</th><th className="text-left px-4 py-2.5">Nota</th><th className="text-left px-4 py-2.5">Pelanggan</th>
                <th className="text-left px-4 py-2.5">Status</th><th className="text-right px-4 py-2.5">Jumlah</th><th className="text-right px-4 py-2.5">Sisa</th><th className="px-4 py-2.5"></th></tr></thead>
              <tbody>{txs.map((t) => <TxRow key={t.id} t={t} onDone={load} bulk={bulk} />)}</tbody>
            </table>
          ) : (
            <table className="w-full text-sm" data-testid="admin-cust-table">
              <thead className="bg-secondary text-xs uppercase text-muted-foreground"><tr>
                <th className="pl-4 py-2.5 w-8"><BulkCheckAll bulk={bulk} entries={cs.map((c) => [`cust:${c.id}`, { kind: "customers", id: c.id }])} testId="bulk-cust-all" /></th>
                <th className="text-left px-4 py-2.5">Pemohon</th><th className="text-left px-4 py-2.5">Nama</th><th className="text-left px-4 py-2.5">No. HP</th><th className="text-left px-4 py-2.5">Alamat</th><th className="px-4 py-2.5"></th></tr></thead>
              <tbody>{cs.map((c) => (
                <tr key={c.id} className="border-t" data-testid={`admin-cust-row-${c.id}`}>
                  <td className="pl-4 py-2.5"><BulkCheck bulk={bulk} k={`cust:${c.id}`} item={{ kind: "customers", id: c.id }} testId={`bulk-cust-${c.id}`} /></td>
                  <td className="px-4 py-2.5 text-xs">{c.owner_name}</td><td className="px-4 py-2.5 font-medium">{c.name}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{c.phone || "-"}</td><td className="px-4 py-2.5 text-xs">{c.address || "-"}</td>
                  <td className="px-4 py-2.5 text-right"><AdminActions editPath={`/admin/records/customers/${c.id}`} deletePath={`/admin/records/customers/${c.id}`} fields={CUST_FIELDS} values={c} onDone={load} title="Edit Customer" testId={`admin-cust-${c.id}`} confirmText="Customer akan dihapus permanen dari Data Customer. Transaksi lama tetap ada." /></td>
                </tr>
              ))}</tbody>
            </table>
          )}
          {(tab === "tx" ? txs : cs).length === 0 && <p className="p-6 text-center text-sm text-muted-foreground" data-testid="admin-tx-empty">{rows === null ? "Memuat..." : "Belum ada data."}</p>}
        </div>
      </Card>
    </div>
  );
}
